import { useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Calendar,
    TrendingUp,
    Clock,
    Target,
    Brain,
    CheckSquare,
    FileText,
    Lightbulb,
    Scale,
    ArrowRight,
    Printer,
    RotateCcw,
    Download
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

export const TradingJournal = ({ className }) => {
    const { language } = useLanguage();
    const journalRef = useRef(null);
    
    const [formData, setFormData] = useState({
        // Section 1: Maklumat Trade
        tanggal: new Date().toISOString().split('T')[0],
        pair: '',
        sesiMarket: '',
        timeframe: '',
        
        // Section 2: Setup & Execution
        jenisSetup: '',
        entry: '',
        stopLoss: '',
        takeProfit: '',
        riskPercent: '',
        
        // Section 3: Psikologi
        psikologi: '',
        
        // Section 4: Keputusan Trade
        result: '',
        exitSebab: '',
        
        // Section 5: Review Ringkas
        betul: '',
        perbaiki: '',
        pelajaran: '',
        
        // Section 6: Disiplin
        disiplin: '',
        
        // Section 7: Action Item
        actionItem: ''
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleReset = () => {
        setFormData({
            tanggal: new Date().toISOString().split('T')[0],
            pair: '',
            sesiMarket: '',
            timeframe: '',
            jenisSetup: '',
            entry: '',
            stopLoss: '',
            takeProfit: '',
            riskPercent: '',
            psikologi: '',
            result: '',
            exitSebab: '',
            betul: '',
            perbaiki: '',
            pelajaran: '',
            disiplin: '',
            actionItem: ''
        });
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className={cn("w-full max-w-3xl mx-auto", className)}>
            {/* Action Buttons - Hidden when printing */}
            <div className="flex items-center justify-between mb-6 print:hidden">
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        className="rounded-xl"
                        data-testid="reset-journal"
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                </div>
                <Button
                    onClick={handlePrint}
                    className="rounded-xl bg-primary hover:bg-primary/90"
                    data-testid="print-journal"
                >
                    <Printer className="w-4 h-4 mr-2" />
                    {language === 'id' ? 'Cetak / PDF' : 'Print / PDF'}
                </Button>
            </div>

            {/* Journal Content */}
            <div 
                ref={journalRef}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden print:rounded-none print:border-0 print:shadow-none"
                data-testid="trading-journal"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-primary to-red-600 px-6 py-5 print:py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-heading font-bold text-white print:text-lg">
                                Trading Journal
                            </h1>
                            <p className="text-red-100 text-sm mt-1">
                                SEKOLAH NUSATRADERS
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-white/80 text-xs">
                                {language === 'id' ? 'Fokus pada proses' : 'Focus on process'}
                            </p>
                            <p className="text-white/80 text-xs">
                                {language === 'id' ? 'Bukan keuntungan' : 'Not profit'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6 print:p-4 print:space-y-4">
                    {/* Section 1: Maklumat Trade */}
                    <section data-testid="section-maklumat">
                        <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                {language === 'id' ? 'Maklumat Trade' : 'Trade Information'}
                            </h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:grid-cols-4">
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    {language === 'id' ? 'Tanggal' : 'Date'}
                                </label>
                                <input
                                    type="date"
                                    value={formData.tanggal}
                                    onChange={(e) => handleInputChange('tanggal', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">Pair / Instrument</label>
                                <input
                                    type="text"
                                    value={formData.pair}
                                    onChange={(e) => handleInputChange('pair', e.target.value)}
                                    placeholder="e.g. EUR/USD"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    {language === 'id' ? 'Sesi Market' : 'Market Session'}
                                </label>
                                <select
                                    value={formData.sesiMarket}
                                    onChange={(e) => handleInputChange('sesiMarket', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                >
                                    <option value="">-</option>
                                    <option value="asia">Asia</option>
                                    <option value="london">London</option>
                                    <option value="newyork">New York</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">Timeframe</label>
                                <select
                                    value={formData.timeframe}
                                    onChange={(e) => handleInputChange('timeframe', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                >
                                    <option value="">-</option>
                                    <option value="M1">M1</option>
                                    <option value="M5">M5</option>
                                    <option value="M15">M15</option>
                                    <option value="M30">M30</option>
                                    <option value="H1">H1</option>
                                    <option value="H4">H4</option>
                                    <option value="D1">D1</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    <hr className="border-slate-100 print:border-slate-200" />

                    {/* Section 2: Setup & Execution */}
                    <section data-testid="section-setup">
                        <div className="flex items-center gap-2 mb-3">
                            <Target className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                Setup & Execution
                            </h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 print:grid-cols-5">
                            <div className="col-span-2 md:col-span-1 print:col-span-1">
                                <label className="text-xs text-slate-500 block mb-1">
                                    {language === 'id' ? 'Jenis Setup' : 'Setup Type'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.jenisSetup}
                                    onChange={(e) => handleInputChange('jenisSetup', e.target.value)}
                                    placeholder="e.g. BOS"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">Entry</label>
                                <input
                                    type="text"
                                    value={formData.entry}
                                    onChange={(e) => handleInputChange('entry', e.target.value)}
                                    placeholder="1.0850"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">Stop Loss</label>
                                <input
                                    type="text"
                                    value={formData.stopLoss}
                                    onChange={(e) => handleInputChange('stopLoss', e.target.value)}
                                    placeholder="1.0820"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">Take Profit</label>
                                <input
                                    type="text"
                                    value={formData.takeProfit}
                                    onChange={(e) => handleInputChange('takeProfit', e.target.value)}
                                    placeholder="1.0900"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">Risk (%)</label>
                                <input
                                    type="text"
                                    value={formData.riskPercent}
                                    onChange={(e) => handleInputChange('riskPercent', e.target.value)}
                                    placeholder="1%"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                        </div>
                    </section>

                    <hr className="border-slate-100 print:border-slate-200" />

                    {/* Section 3: Psikologi */}
                    <section data-testid="section-psikologi">
                        <div className="flex items-center gap-2 mb-3">
                            <Brain className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                {language === 'id' ? 'Psikologi' : 'Psychology'}
                            </h2>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {[
                                { value: 'tenang', label: language === 'id' ? 'Tenang' : 'Calm', color: 'emerald' },
                                { value: 'ragu', label: language === 'id' ? 'Sedikit ragu' : 'Slightly doubtful', color: 'amber' },
                                { value: 'emosi', label: language === 'id' ? 'Emosi terganggu' : 'Emotionally disturbed', color: 'rose' }
                            ].map((option) => (
                                <label
                                    key={option.value}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all print:px-3 print:py-1",
                                        formData.psikologi === option.value
                                            ? option.color === 'emerald' 
                                                ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                                : option.color === 'amber'
                                                    ? "bg-amber-50 border-amber-300 text-amber-700"
                                                    : "bg-rose-50 border-rose-300 text-rose-700"
                                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name="psikologi"
                                        value={option.value}
                                        checked={formData.psikologi === option.value}
                                        onChange={(e) => handleInputChange('psikologi', e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className={cn(
                                        "w-4 h-4 rounded border-2 flex items-center justify-center",
                                        formData.psikologi === option.value
                                            ? option.color === 'emerald'
                                                ? "bg-emerald-500 border-emerald-500"
                                                : option.color === 'amber'
                                                    ? "bg-amber-500 border-amber-500"
                                                    : "bg-rose-500 border-rose-500"
                                            : "border-slate-300"
                                    )}>
                                        {formData.psikologi === option.value && (
                                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="text-sm font-medium">{option.label}</span>
                                </label>
                            ))}
                        </div>
                    </section>

                    <hr className="border-slate-100 print:border-slate-200" />

                    {/* Section 4: Keputusan Trade */}
                    <section data-testid="section-keputusan">
                        <div className="flex items-center gap-2 mb-3">
                            <CheckSquare className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                {language === 'id' ? 'Keputusan Trade' : 'Trade Outcome'}
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2">
                            <div>
                                <label className="text-xs text-slate-500 block mb-2">Result</label>
                                <div className="flex gap-2">
                                    {[
                                        { value: 'win', label: 'Win', color: 'emerald' },
                                        { value: 'loss', label: 'Loss', color: 'rose' },
                                        { value: 'be', label: 'BE', color: 'slate' }
                                    ].map((option) => (
                                        <label
                                            key={option.value}
                                            className={cn(
                                                "flex-1 text-center px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm font-medium print:px-2 print:py-1",
                                                formData.result === option.value
                                                    ? option.color === 'emerald'
                                                        ? "bg-emerald-500 border-emerald-500 text-white"
                                                        : option.color === 'rose'
                                                            ? "bg-rose-500 border-rose-500 text-white"
                                                            : "bg-slate-500 border-slate-500 text-white"
                                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                name="result"
                                                value={option.value}
                                                checked={formData.result === option.value}
                                                onChange={(e) => handleInputChange('result', e.target.value)}
                                                className="sr-only"
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-2">
                                    {language === 'id' ? 'Exit Sebab' : 'Exit Reason'}
                                </label>
                                <div className="flex gap-2">
                                    {[
                                        { value: 'tp', label: 'TP' },
                                        { value: 'sl', label: 'SL' },
                                        { value: 'manual', label: 'Manual' }
                                    ].map((option) => (
                                        <label
                                            key={option.value}
                                            className={cn(
                                                "flex-1 text-center px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm font-medium print:px-2 print:py-1",
                                                formData.exitSebab === option.value
                                                    ? "bg-primary border-primary text-white"
                                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                name="exitSebab"
                                                value={option.value}
                                                checked={formData.exitSebab === option.value}
                                                onChange={(e) => handleInputChange('exitSebab', e.target.value)}
                                                className="sr-only"
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <hr className="border-slate-100 print:border-slate-200" />

                    {/* Section 5: Review Ringkas */}
                    <section data-testid="section-review">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                {language === 'id' ? 'Review Ringkas' : 'Quick Review'}
                            </h2>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    {language === 'id' ? 'Apa yang dibuat dengan betul?' : 'What was done correctly?'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.betul}
                                    onChange={(e) => handleInputChange('betul', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    {language === 'id' ? 'Apa yang boleh diperbaiki?' : 'What can be improved?'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.perbaiki}
                                    onChange={(e) => handleInputChange('perbaiki', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    {language === 'id' ? 'Pelajaran hari ini (1 ayat)' : "Today's lesson (1 sentence)"}
                                </label>
                                <input
                                    type="text"
                                    value={formData.pelajaran}
                                    onChange={(e) => handleInputChange('pelajaran', e.target.value)}
                                    maxLength={100}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                                />
                            </div>
                        </div>
                    </section>

                    <hr className="border-slate-100 print:border-slate-200" />

                    {/* Section 6: Disiplin */}
                    <section data-testid="section-disiplin">
                        <div className="flex items-center gap-2 mb-3">
                            <Scale className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                {language === 'id' ? 'Disiplin' : 'Discipline'}
                            </h2>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {[
                                { value: 'ikut', label: language === 'id' ? 'Ikut plan' : 'Followed plan', color: 'emerald' },
                                { value: 'langgar', label: language === 'id' ? 'Langgar plan' : 'Violated plan', color: 'rose' },
                                { value: 'overtrade', label: 'Overtrade', color: 'amber' }
                            ].map((option) => (
                                <label
                                    key={option.value}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all print:px-3 print:py-1",
                                        formData.disiplin === option.value
                                            ? option.color === 'emerald'
                                                ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                                : option.color === 'rose'
                                                    ? "bg-rose-50 border-rose-300 text-rose-700"
                                                    : "bg-amber-50 border-amber-300 text-amber-700"
                                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name="disiplin"
                                        value={option.value}
                                        checked={formData.disiplin === option.value}
                                        onChange={(e) => handleInputChange('disiplin', e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className={cn(
                                        "w-4 h-4 rounded border-2 flex items-center justify-center",
                                        formData.disiplin === option.value
                                            ? option.color === 'emerald'
                                                ? "bg-emerald-500 border-emerald-500"
                                                : option.color === 'rose'
                                                    ? "bg-rose-500 border-rose-500"
                                                    : "bg-amber-500 border-amber-500"
                                            : "border-slate-300"
                                    )}>
                                        {formData.disiplin === option.value && (
                                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="text-sm font-medium">{option.label}</span>
                                </label>
                            ))}
                        </div>
                    </section>

                    <hr className="border-slate-100 print:border-slate-200" />

                    {/* Section 7: Action Item */}
                    <section data-testid="section-action">
                        <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="w-4 h-4 text-primary" />
                            <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">
                                Action Item
                            </h2>
                        </div>
                        <div className="flex items-start gap-3">
                            <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-2" />
                            <input
                                type="text"
                                value={formData.actionItem}
                                onChange={(e) => handleInputChange('actionItem', e.target.value)}
                                placeholder={language === 'id' 
                                    ? 'Satu tindakan konkrit untuk trade seterusnya...'
                                    : 'One concrete action for next trade...'
                                }
                                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1"
                            />
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 print:bg-white print:py-3">
                    <p className="text-center text-sm text-slate-500 italic">
                        &ldquo;{language === 'id' 
                            ? 'Disiplin harian mencipta konsistensi jangka panjang.'
                            : 'Daily discipline creates long-term consistency.'
                        }&rdquo;
                    </p>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    [data-testid="trading-journal"],
                    [data-testid="trading-journal"] * {
                        visibility: visible;
                    }
                    [data-testid="trading-journal"] {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .print\\:hidden {
                        display: none !important;
                    }
                }
            `}</style>
        </div>
    );
};
